import { openResponseLacksLayoutVisibilityStates } from '@/common/utils/startupDialogOnLoadCatchUp'
import type { ScriptData } from '@/components/platform/types'
import { extractScriptNodeText } from '@/common/utils/scriptNodeText'
import { getWebClientExecFromScriptNode } from '@/common/utils/webClientExec'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import {
  isStartupMainFramePageData,
  STARTUP_ACTION_FLAG,
} from '@/common/utils/mainFrameSlot'

const readText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
  }
  if (value && typeof value === 'object' && '_text' in (value as Record<string, unknown>)) {
    return readText((value as { _text?: unknown })._text)
  }
  return null
}

function pickRootFormOnLoadNode(formLike: unknown): ScriptData | undefined {
  if (!formLike || typeof formLike !== 'object') {
    return undefined
  }

  const form = formLike as Record<string, unknown>
  const candidates = [form.onLoad, (form.form as Record<string, unknown> | undefined)?.onLoad]
  return candidates.find(
    (candidate) =>
      candidate != null &&
      typeof candidate === 'object' &&
      extractScriptNodeText(candidate).trim() !== '',
  ) as ScriptData | undefined
}

export function shouldRunRootFormOnLoadOnClientBeforeBuild(formLike: unknown): boolean {
  const node = pickRootFormOnLoadNode(formLike)
  if (!node) return false
  return getWebClientExecFromScriptNode(node)
}

export function resolveWebClientRootOnLoadScriptAfterReload(formLike: unknown): string | null {
  if (!shouldRunRootFormOnLoadOnClientBeforeBuild(formLike)) {
    return null
  }
  const script = readRootFormOnLoadScript(formLike)
  if (!script?.trim()) {
    return null
  }
  return script
}

export function readRootFormOnLoadScript(formLike: unknown): string | null {
  const node = pickRootFormOnLoadNode(formLike)
  if (node) {
    return extractScriptNodeText(node)
  }
  if (!formLike || typeof formLike !== 'object') {
    return null
  }
  const form = formLike as Record<string, unknown>
  return readText(form.onLoad) ?? readText((form.form as Record<string, unknown> | undefined)?.onLoad)
}

export function readRootFormFunctions(formLike: unknown): string | null {
  if (!formLike || typeof formLike !== 'object') {
    return null
  }

  const form = formLike as Record<string, unknown>
  return readText(form.functions) ?? readText((form.form as Record<string, unknown> | undefined)?.functions)
}

/** Root onLoad for En.run: `<functions>` then onLoad body. Empty functions → body only. */
export function combineRootOnLoadScript(
  functions: string | null | undefined,
  script: string,
): string {
  const fn = String(functions ?? '').trim()
  const body = String(script ?? '')
  return fn ? `${fn}\n${body}` : body
}

/**
 * En.run prepend of form.functions. Skip if cmd already contains the same block
 * (onLoad may pass combineRootOnLoadScript).
 */
export function prependFormFunctionsUnlessPresent(
  functions: string | null | undefined,
  cmd: string,
): string {
  const common = String(functions ?? '').trim()
  const body = String(cmd ?? '')
  if (!common) return body
  if (body.includes(common)) return body
  return `${common}\n${body}`
}

export type RootOnLoadDedupKeyInput = {
  slotId: unknown
  registryFormId: unknown
  event: unknown
  preOpenId: unknown
  activateId: unknown
  /** При :activate новый sessionId = новый прогон onLoad (SAME_WINDOW в dialog). */
  sessionId?: unknown
  /**
   * en.reloadForm / STACK_ACTION: bump so Form.vue re-runs web_client_exec onLoad once
   * (without racing a second GlobalProvider catch-up on the shared En instance).
   */
  rootOnLoadNonce?: unknown
}

/** Stable once-per-activate key; excludes fields that change during onLoad side effects. */
export function buildRootOnLoadDedupKey(input: RootOnLoadDedupKeyInput): string {
  const base: Record<string, unknown> = {
    slotId: input.slotId,
    registryFormId: input.registryFormId,
    event: input.event,
    preOpenId: input.preOpenId,
    activateId: input.activateId,
  }
  const sessionId =
    input.event === ':activate' && input.sessionId != null
      ? String(input.sessionId).trim()
      : ''
  if (sessionId) {
    base.sessionId = sessionId
  }
  const nonce =
    input.rootOnLoadNonce != null ? String(input.rootOnLoadNonce).trim() : ''
  if (nonce) {
    base.rootOnLoadNonce = nonce
  }
  return JSON.stringify(base)
}

export function buildRootOnLoadRunTrigger(
  dedupKey: string,
  script: string | null | undefined,
  functions: string | null | undefined,
): string {
  if (!dedupKey || !script) {
    return ''
  }
  return `${dedupKey}|${script.length}|${(functions ?? '').length}`
}

export function onLoadScriptUsesFormFunctionsBlock(script: string): boolean {
  return /(?:^|[;\n}\s])(?!en\.|importClass|console\.|await\s|if\s|for\s|while\s|switch\s|return\s)[A-Za-z_$][\w$]*\s*\(/m.test(
    script,
  )
}

export function isRootFormOnLoadReady(input: {
  script: string
  functions: string | null | undefined
}): boolean {
  const { script, functions } = input
  if (onLoadScriptUsesFormFunctionsBlock(script) && !String(functions ?? '').trim()) {
    return false
  }
  return true
}

/** Первый page id из разметки main (executeScript ON_LOAD на level PAGE). */
export function resolveRootFormPageIdFromLayout(formLike: unknown): number {
  if (!formLike || typeof formLike !== 'object') {
    return 0
  }
  const main = (formLike as Record<string, unknown>).main
  if (!Array.isArray(main)) {
    return 0
  }
  for (const region of main) {
    if (!Array.isArray(region)) {
      continue
    }
    for (const tab of region) {
      if (!tab || typeof tab !== 'object') {
        continue
      }
      const tabRec = tab as Record<string, unknown>
      const attrs =
        (tabRec._attributes as Record<string, unknown> | undefined) ??
        (tabRec.attributes as Record<string, unknown> | undefined)
      const rawId = attrs?.id
      if (rawId == null || String(rawId).trim() === '') {
        continue
      }
      const parsed = parseInt(String(rawId), 10)
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed
      }
    }
  }
  return 0
}

export function isStartupActionPageData(pageData: unknown): boolean {
  if (pageData == null || typeof pageData !== 'object') {
    return false
  }
  return (pageData as Record<string, unknown>)[STARTUP_ACTION_FLAG] === true
}

export function shouldRunRootFormOnLoadForPage(input: {
  event: unknown
  pageData: unknown
}): boolean {
  if (
    input.event === ':activate' ||
    isStartupMainFramePageData(input.pageData) ||
    isStartupActionPageData(input.pageData)
  ) {
    return true
  }

  const pageData = input.pageData as Record<string, unknown> | undefined
  const form = pageData?.form
  if (!shouldRunRootFormOnLoadOnClientBeforeBuild(form)) {
    return false
  }

  // web_client_exec: middle не шлёт states — догоняем на клиенте, если pre-build не записал visibility.
  return openResponseLacksLayoutVisibilityStates(pageData ?? {})
}

/** En.run prepends pageData.form.functions; dialogClosed runs on dialog page — attach parent helpers. */
export function buildEnPageDataWithParentFormFunctions(
  parentPage: unknown,
  dialogPage: unknown,
): Record<string, unknown> {
  const parentFunctions = readRootFormFunctions(parentPage)
  const parent =
    parentPage && typeof parentPage === 'object' && !Array.isArray(parentPage)
      ? (parentPage as Record<string, unknown>)
      : {}
  const dialog =
    dialogPage && typeof dialogPage === 'object' && !Array.isArray(dialogPage)
      ? (dialogPage as Record<string, unknown>)
      : {}
  // Parent is the reload/session target (form 2610); keep dialog only for form XML merge.
  if (!parentFunctions) {
    return Object.keys(parent).length > 0 ? { ...parent } : dialog
  }
  const parentForm =
    parent.form && typeof parent.form === 'object' && !Array.isArray(parent.form)
      ? (parent.form as Record<string, unknown>)
      : {}
  const dialogForm =
    dialog.form && typeof dialog.form === 'object' && !Array.isArray(dialog.form)
      ? (dialog.form as Record<string, unknown>)
      : {}
  return {
    ...parent,
    form: {
      ...dialogForm,
      ...parentForm,
      functions: parentFunctions,
    },
  }
}

/** Element names scripts touch before registry-backed operations. */
const SET_VISIBLE_LEVELS = new Set(['panel', 'action', 'element', 'overlay', 'page'])

export function extractScriptRegistryElementNames(script: string): string[] {
  const combined = String(script ?? '')
  const names = new Set<string>()
  for (const m of combined.matchAll(/reloadElement\s*\(\s*["']([^"']+)["']/g)) {
    const name = m[1]?.trim()
    if (name) names.add(name)
  }
  for (const m of combined.matchAll(/getRegistryItemByName\s*\(\s*[^,]+,\s*["']([^"']+)["']/g)) {
    const name = m[1]?.trim()
    if (name) names.add(name)
  }
  for (const m of combined.matchAll(/putFullData\s*\(\s*["']([^"']+)["']/g)) {
    const name = m[1]?.trim()
    if (name) names.add(name)
  }
  for (const m of combined.matchAll(/setVisible\s*\(\s*["']([^"']+)["']\s*,/g)) {
    const name = m[1]?.trim()
    if (name && !SET_VISIBLE_LEVELS.has(name.toLowerCase())) {
      names.add(name)
    }
  }
  // en.setVisible("element", "fieldName", …) — second arg is registry name
  for (const m of combined.matchAll(
    /setVisible\s*\(\s*["']element["']\s*,\s*["']([^"']+)["']/gi,
  )) {
    const name = m[1]?.trim()
    if (name) names.add(name)
  }
  return [...names]
}

/** Platform action ids from en.setVisible("action", id, …) in onLoad/onClick scripts. */
export function extractScriptRegistryActionIds(script: string): number[] {
  const ids = new Set<number>()
  for (const match of String(script ?? '').matchAll(
    /setVisible\s*\(\s*["']action["']\s*,\s*(\d+)/gi,
  )) {
    const parsed = parseInt(String(match[1] ?? ''), 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      ids.add(parsed)
    }
  }
  return [...ids]
}

export async function waitForRegistryActions(
  getActions: () => Record<string, unknown>,
  actionIds: number[],
  options?: { timeoutMs?: number; intervalMs?: number },
): Promise<boolean> {
  const timeoutMs = options?.timeoutMs ?? 3000
  const intervalMs = options?.intervalMs ?? 50
  if (!actionIds.length) return true
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const registry = getActions()
    const ready = actionIds.every((actionId) => {
      const entry = registry[String(actionId)] as { setVisible?: unknown } | undefined
      return !!entry && typeof entry.setVisible === 'function'
    })
    if (ready) return true
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  return false
}

export async function waitForRegistryElements(
  getElements: () => Record<string, unknown>,
  names: string[],
  options?: { timeoutMs?: number; intervalMs?: number },
): Promise<boolean> {
  const timeoutMs = options?.timeoutMs ?? 3000
  const intervalMs = options?.intervalMs ?? 50
  if (!names.length) return true
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const elements = getElements()
    const elementReady = (el: {
      reload?: unknown
      getActionByColumnName?: unknown
      getData?: unknown
      setVisible?: unknown
    } | null | undefined) =>
      !!el &&
      (typeof el.reload === 'function' ||
        typeof el.getActionByColumnName === 'function' ||
        typeof el.getData === 'function' ||
        // Labels / simple elements: only setVisible (emailNotConfirmedLabel4292).
        typeof el.setVisible === 'function')

    const ready = names.every((name) => {
      if (elementReady(elements[name] as { setVisible?: unknown } | undefined)) return true
      // registry key may be `name#elementId` (simple Label)
      return Object.values(elements).some((entry) => {
        const el = entry as { fieldName?: string; setVisible?: unknown } | undefined
        return (
          String(el?.fieldName ?? '').trim() === name &&
          elementReady(el)
        )
      })
    })
    if (ready) return true
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  return false
}

const ROOT_ON_LOAD_GUARD_KEY = '__workflowRootOnLoadActions'
const ROOT_ON_LOAD_GUARD_TTL_MS = 10_000
const executedRootOnLoadActions = (() => {
  const globalStore = globalThis as typeof globalThis & Record<string, unknown>
  const existing = globalStore[ROOT_ON_LOAD_GUARD_KEY]
  if (existing instanceof Map) {
    return existing as Map<string, number>
  }
  const created = new Map<string, number>()
  globalStore[ROOT_ON_LOAD_GUARD_KEY] = created
  return created
})()

const isFreshRootOnLoadGuard = (timestamp: number): boolean =>
  Number.isFinite(timestamp) && Date.now() - timestamp < ROOT_ON_LOAD_GUARD_TTL_MS

/** Same-schema STACK: slot + registry form id unchanged (empty incoming schema = keep current). */
export function isSameSchemaStackReload(
  currentRegistryFormId: string,
  incomingSchemaFormId: string,
): boolean {
  const current = String(currentRegistryFormId ?? '').trim()
  const incoming = String(incomingSchemaFormId ?? '').trim()
  if (!current) return false
  return !incoming || incoming === current
}

export function isRootOnLoadGloballyReserved(key: string): boolean {
  const timestamp = executedRootOnLoadActions.get(key)
  if (timestamp != null && isFreshRootOnLoadGuard(timestamp)) {
    return true
  }
  if (timestamp != null) {
    executedRootOnLoadActions.delete(key)
  }
  return false
}

export function reserveRootOnLoadGlobally(key: string): void {
  executedRootOnLoadActions.set(key, Date.now())
}

export function releaseRootOnLoadGlobally(key: string): void {
  executedRootOnLoadActions.delete(key)
}

/** Dedup trigger matching Form.vue root onLoad (nonce + script + field snapshot). */
export function buildRootOnLoadRunTriggerFromPage(
  pageData: Record<string, unknown> | null | undefined,
  slotId: string,
  registryFormId: string,
): string {
  const form = pageData?.form
  const fields = (pageData ? mergePageDataFieldSnapshot(pageData) : {}) as Record<string, any>
  const event = fields._event?.value ?? fields._event
  return buildRootOnLoadRunTrigger(
    buildRootOnLoadDedupKey({
      slotId,
      registryFormId,
      event,
      preOpenId: fields.preOpenId?.value ?? fields.preOpenId,
      activateId: fields._id?.value ?? fields._id,
      sessionId: pageData?.sessionId,
      rootOnLoadNonce: pageData?.$rootOnLoadNonce,
    }),
    readRootFormOnLoadScript(form),
    readRootFormFunctions(form),
  )
}
